<template>
  <v-content class="referrals page">
    <can I="list" a="Referral">
      <v-container>
        <v-toolbar flat class="transparent">
          <v-breadcrumbs
            class="pl-0"
            :items="[
              { exact: true, text: 'Admin', to: '/dashboard' },
              { exact: true, text: 'Referrals', to: '/referrals' },
            ]"
          >
            <v-icon slot="divider">mdi-chevron-right</v-icon>
          </v-breadcrumbs>

          <v-spacer />

          <can I="create" a="Referral">
            <v-tooltip bottom>
              <v-btn slot="activator" flat icon @click="filter">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <span>Add Referral</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-btn small flat fab slot="activator" to="/referrals/new">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
              <span>Add Referral</span>
            </v-tooltip>
          </can>
        </v-toolbar>

        <!-- Heading and Search -->
        <v-card toolbar>
          <v-toolbar flat class="transparent">
            <v-card-title class="headline"> Referrals </v-card-title>

            <v-spacer />

            <v-text-field
              v-model="filters.name"
              placeholder="Search"
              prepend-icon="mdi-magnify"
              @keyup.enter="filter"
              type="search"
              clearable
            />
            <v-btn color="secondary" @click="filter">Filter</v-btn>
            <v-btn small flat fab
              :color="filtersApplied ? 'accent' : ''"
              @click="showFilters = !showFilters"
            >
              <v-icon>mdi-filter-variant</v-icon>
            </v-btn>
          </v-toolbar>

          <!-- Filters -->
          <transition name="slide-y-transition">
            <v-card-text v-show="showFilters">
              <OpportunitySelect clearable v-model="filters.opportunity" @input="filter" />
              <OrganizationSelect clearable v-model="filters.organization" @input="filter" />
            </v-card-text>
          </transition>

          <!-- Table -->
          <v-data-table
            :headers="headers"
            :items="referrals"
            :loading="loading"
            :total-items="total"
            :pagination.sync="pagination"
            @update:pagination="filter"
            class="elevation-2"
            must-sort
          >
            <template slot="items" slot-scope="{ item: referral }">
              <td>{{ referral.id }}</td>
              <td>
                <router-link :to="`/profiles/${referral.profile.id}`">
                  {{ referral.profile.fullName }}
                </router-link>
              </td>
              <td>
                <router-link :to="`/opportunities/${referral.opportunity.id}`">
                  {{ referral.opportunity.name }}
                </router-link>
              </td>
              <td>
                <span v-if="referral.referrer">{{ referral.referrer.fullName }}</span>
                <span v-else>Leaply Platform</span>
              </td>
              <td>
                <span v-if="referral.referredAt">
                  {{ new Date(referral.referredAt) | date('h:mm a, MMMM do yyyy') }}
                </span>
                <span v-else>Missing Date</span>
              </td>
              <td>
                <v-tooltip left color="grey darken-3">
                  <span slot="activator">
                    <v-btn color="success" small icon flat v-if="referral.opportunityApplication">
                      <v-icon>mdi-check-circle-outline</v-icon>
                    </v-btn>
                    <v-btn small icon flat color="grey" v-else>
                      <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                  </span>
                  <span v-if="referral.opportunityApplication"> Applied </span>
                  <span v-else>Pending</span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </can>
  </v-content>
</template>

<script>
// Components
import OpportunitySelect from '@/components/selects/OpportunitySelect'
import OrganizationSelect from '@/components/selects/OrganizationSelect'

// Helpers
import _ from 'lodash'
import { mapState } from 'vuex'

// Vue Component
export default {
  name: 'ReferralList',
  components: { OpportunitySelect, OrganizationSelect },
  data() {
    return {
      // Search and filters
      search: _.get(this.$route, 'query.search') || '',
      showFilters: _.get(this.$route, 'query.profile'),
      filters: {
        name: this.$route.query.name
          ? _.get(this.$route, 'query.name')
          : null,
        opportunity: this.$route.query.opportunity
          ? parseInt(_.get(this.$route, 'query.opportunity'))
          : null,
        organization: this.$route.query.organization
          ? parseInt(_.get(this.$route, 'query.organization'))
          : null,
      },
      // Table Configuration
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Profile', value: 'profile_last_name', sortable: false },
        { text: 'Opportunity', value: 'opportunity_name', sortable: false },
        { text: 'Referrer', value: 'referrer_id' },
        { text: 'Referred At', value: 'referred_at' },
        { text: 'Status', value: 'opportunity_application_id' },
      ],
      pagination: {
        sortBy: 'referred_at',
        descending: true,
        page: 1,
        rowsPerPage: 25,
      },
    }
  },
  computed: {
    ...mapState('admin/referrals', ['active', 'loading', 'total', 'page', 'rowsPerPage']),
    referrals() {
      if (!this.active) return []
      return this.active
    },
    filtersApplied() {
      return !!this.filters.opportunity || !!this.filters.organization
    },
  },
  methods: {
    filter(payloadOrEvent) {
      // Update URL
      const filters = Object.assign({}, this.filters, { search: this.search })
      this.$router.replace(Object.assign({}, this.$route, { query: _.pickBy(filters, _.identity) }))
      // Fetch new data that matches filters and search
      this.$store.dispatch(
        'admin/referrals/search',
        Object.assign(
          { search: this.search },
          { filters: this.filters },
          typeof payloadOrEvent == 'KeyboardEvent' ? {} : payloadOrEvent
        )
      )
    },
  },
}
</script>
